import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTeacher(data.teacher);
      } else {
        setTeacher(null);
      }
    } catch {
      setTeacher(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // TpT lightweight account — classroom name only, no email/password
  const tptArrive = async (classroomName, novelSlug) => {
    const res = await fetch('/api/auth/tpt-arrive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ classroom_name: classroomName, novel_slug: novelSlug }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create account');
    setTeacher(data.teacher);
    return data;
  };

  // TpT unlock for an already-logged-in teacher arriving via UTM link
  const tptUnlock = async (novelSlug) => {
    const res = await fetch('/api/auth/tpt-unlock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ novel_slug: novelSlug }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to unlock novel');
    // Refresh teacher data so tpt_novels list is up to date
    await fetchMe();
    return data;
  };

  // Full account signup (email + password) — organic path or TpT upgrade
  const signup = async (email, password, classroomName, agreedToTerms, upgradeFromSession = false) => {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        classroom_name: classroomName,
        agreed_to_terms: agreedToTerms,
        upgrade_from_session: upgradeFromSession,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Signup failed');
    setTeacher(data.teacher);
    return data.teacher;
  };

  const login = async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    setTeacher(data.teacher);
    return data.teacher;
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setTeacher(null);
  };

  const forgotPassword = async (email) => {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
    return data;
  };

  const resetPassword = async (token, password) => {
    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
    return data;
  };

  const updateTeacher = (updates) => {
    setTeacher((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  // Derived state
  const isLightweightAccount = teacher && !teacher.email;
  const isSubscriber = teacher?.is_subscriber === true;
  const hasTptNovels = teacher?.tpt_novels?.length > 0;

  return (
    <AuthContext.Provider value={{
      teacher,
      loading,
      tptArrive,
      tptUnlock,
      signup,
      login,
      logout,
      updateTeacher,
      fetchMe,
      forgotPassword,
      resetPassword,
      isLightweightAccount,
      isSubscriber,
      hasTptNovels,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
